import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Eye, Edit, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import CompanyCollaboratorDetailView from './company-collaborator-detail-view';
import CompanyCollaboratorEditView from './company-collaborator-edit-view';
import companyStore from "../../store/company-store";
import professionalStore from "../../store/professional-store";
import CompanyAddCollaboratorModal from "./company-add-collaborator-modal";
import CollaboratorDeleteModal from "./collaborator-delete-modal";
import companyCollaboratorsViewModel from '../../view-model/professional/company/company-collaborators-view-model';
import { useSubscriptionLimitContext } from '../subscription/subscription-limit-provider';

interface CompanyCollaboratorsProps {
  companyId?: string;
  companyName?: string;
  onBack: () => void;
}

const CompanyCollaborators: React.FC<CompanyCollaboratorsProps> = observer(({ companyId, companyName, onBack }) => {
  const { t } = useTranslation();
  const vm = companyCollaboratorsViewModel;
  const { checkCollaboratorLimit } = useSubscriptionLimitContext();

  // Handler qui vérifie la limite avant d'ouvrir le modal d'ajout de collaborateur
  const handleAddCollaboratorWithCheck = async () => {
    const canCreate = await checkCollaboratorLimit();
    if (canCreate) {
      vm.setIsAddCollaboratorModalOpen(true);
    }
    // Si la limite est atteinte, le modal de limite s'affiche automatiquement
  };

  if(!companyStore.getCompanyById || !companyStore.getCompanyById.collaboratorCompanies) {
    return <div>{t('company.collaborators.noCompanySelected', 'Aucune entreprise sélectionnée')}</div>;
  }

  // Si on est en mode détail, afficher la vue détaillée du collaborateur
  if (vm.viewMode === 'detail' && vm.selectedCollaborator) {
    return (
      <CompanyCollaboratorDetailView
        onBack={vm.handleBackToList}
        onEdit={() => vm.setViewMode('edit')}
      />
    );
  }

  // Si on est en mode édition, afficher la vue d'édition du collaborateur
  if (vm.viewMode === 'edit' && vm.selectedCollaborator) {
    return (
      <CompanyCollaboratorEditView
        onBack={() => vm.setViewMode('detail')}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={onBack}
            className="text-primary hover:underline mb-2 inline-flex items-center"
          >
            ← {t('company.collaborators.back', 'Retour aux entreprises')}
          </button>
          <h2 className="text-xl font-semibold">
            {t('company.collaborators.title', 'Collaborateurs')} - {companyName}
          </h2>
        </div>
        <button
          onClick={handleAddCollaboratorWithCheck}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          <Plus size={16} />
          <span>{t('company.collaborators.add', 'Ajouter un collaborateur')}</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.collaborators.collaborator', 'Collaborateur')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.collaborators.email', 'Email')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.collaborators.role', 'Rôle')}
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('company.collaborators.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {companyStore.getCompanyById?.collaboratorCompanies?.map((collaborator) => (
              <tr key={collaborator.collaboratorCompagnieId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {collaborator.avatar ? (
                        <img className="h-10 w-10 rounded-full object-cover" src={collaborator.avatar} alt={`${collaborator.firstName} ${collaborator.lastName}`} />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <span className="text-gray-500 dark:text-gray-400 font-medium">
                            {collaborator.firstName.charAt(0)}{collaborator.lastName.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {collaborator.firstName} {collaborator.lastName}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-300">{collaborator.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${vm.getRoleBadgeClass(collaborator.role)}`}>
                    {t(`company.collaborators.roles.${collaborator.role.toLowerCase()}`, collaborator.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                        onClick={() => vm.handleViewCollaborator(collaborator)}
                        className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                        title={String(t('company.list.view', 'Visualiser'))}
                    >
                      <Eye size={16}/>
                    </button>

                    {companyStore.isCurrentUserAdmin && collaborator.collaboratorId !== professionalStore.getId && (
                        <button
                            onClick={() => vm.handleEditCollaborator(collaborator)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            title={String(t('company.list.edit', 'Modifier'))}
                        >
                          <Edit size={16}/>
                        </button>
                    )}

                    {companyStore.isCurrentUserAdmin && collaborator.collaboratorId !== professionalStore.getId && (
                        <button
                            onClick={() => vm.handleOpenDeleteModal(collaborator)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 size={16}/>
                        </button>
                    )}
                  </div>

                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CompanyAddCollaboratorModal
          isOpen={vm.isAddCollaboratorModalOpen}
          onClose={() => {
            vm.setIsAddCollaboratorModalOpen(false);
            vm.setError('');
          }}
          onAddCollaborator={(data) => vm.handleAddCollaborator(data)}
      />

      {vm.isDeleteCollaboratorModalOpen && vm.collaboratorToDelete && (
        <CollaboratorDeleteModal
          collaboratorName={`${vm.collaboratorToDelete.firstName} ${vm.collaboratorToDelete.lastName}`}
          onConfirm={vm.handleRemoveCollaborator}
          onCancel={vm.handleCloseDeleteModal}
          isDeleting={vm.isDeleting}
        />
      )}
    </div>
  );
});

export default CompanyCollaborators;
